/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInstance, getEditInstance, newInstance, singleResource } from "./";
import { selectUser } from "../store/userSlice";

const useInstance = (resource, id, action) => {
  const [instance, setInstance] = useState(null);
  const [instanceDetails, setInstanceDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const routeParams = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const parent_id = queryParams.get('p');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  // fetch the instance data
  const handleInstanceAction = async (crudAction, method) => {
    try {
      // get the instance data
      let response = await dispatch(crudAction).unwrap();
      // set the user id to the current user
      response.user = user.id;
      // set the instance details
      handleInstanceDetails(response, resource, action);
      // set the instance data
      setInstance(response);
      // navigate to the view if saving a new instance

      if (method === 'post') {
        if (resource === 'projects' || resource === 'organizations' || resource === 'users') {
          navigate(`../${resource}`);
        } else {
          navigate(`../${resource}/${response.id}`);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // determine the instance action based on the resource
  const handleGetInstance = () => {
    setInstanceDetails(null);
    setInstance(null);
    setLoading(true);

    if (action === 'view' || action === 'comments' || action === 'imi-comments' || action === 'pre-trial-comments' || action === 'status' || action === 'version' || action === 'export') {
      handleInstanceAction(getInstance(resource)(id), 'get');
    }
    if (action === 'edit') {
      handleInstanceAction(id === 'new' ? newInstance(resource)() : getEditInstance(resource)(id), 'get');
    }
  }

  const handleInstanceDetails = (response, resource, action) => {
    // define the instance details object
    let details = {};
    // determine what type of action is being performed and set the title and brief accordingly
    if (action === 'view' || action === 'comments' || action === 'imi-comments' || action === 'pre-trial-comments' || action === 'status' || action === 'version' || action === 'export') {
      details.title = response.title;
    }
    if (action === 'edit') {
      if (id === 'new') {
        details.title = 'New ' + singleResource(resource);
      } else {
        details.title = 'Edit ' + singleResource(resource);
      }
    }
    details.parent = handleInstanceParent(response, resource);
    // set the return path
    details.return = handleInstanceReturn(response, resource, action);
    // set the instance details
    setInstanceDetails(details);
  }

  const handleInstanceParent = (response, resource) => {
    let parent;
    switch (resource) {
      case "courses":
        parent = {
          id: response.project.id,
          resource: "projects"
        };
        break;
      case "lessons":
        parent = {
          id: response.course.id,
          resource: "courses"
        };
        break;
      case "sections":
        parent = {
          id: response.lesson.id,
          resource: "lessons"
        };
        break;
      case "pages":
        parent = {
          id: response.section.id,
          resource: "sections"
        };
        break;
      case "templates":
        parent = null;
        break;
      case "users":
        parent = null;
        break;
      default:
        parent = null;
        break;
    }
    return parent;
  }

  const handleInstanceReturn = (response, resource, action) => {
    let returnObject = {};
    if (action === 'edit' && id !== 'new') {
      if (resource === 'projects' || resource === 'organizations' || resource === 'users') {
        returnObject.path = `../${resource}`;
        returnObject.label = 'Back to ' + singleResource(resource) + 's';
      } else {
        returnObject.path =  `../${resource}/${response.id}`;
        returnObject.label = 'Back to ' + singleResource(resource);
      }
    } else if (action === 'comments' || action === 'imi-comments' || action === 'pre-trial-comments') {
      returnObject.path =  `../${resource}/${response.id}`;
      returnObject.label = 'Back to ' + singleResource(resource);
    } else {
      switch (resource) {
        case "courses":
          returnObject.path = '../projects';
          returnObject.label = 'Back to Project'
          break;
        case "lessons":
          returnObject.path =  id === 'new' ? `../courses/${parent_id}` : `../courses/${response.course.id}`;
          returnObject.label = 'Back to Course';
          break;
        case "sections":
          returnObject.path =  id === 'new' ? `../lessons/${parent_id}` : `../lessons/${response.lesson.id}`;
          returnObject.label = 'Back to Lesson';
          break;
        case "pages":
          returnObject.path =  id === 'new' ? `../sections/${parent_id}` : `../sections/${response.section.id}`;
          returnObject.label = 'Back to Section';
          break;
        default:
          returnObject.path = `../${resource}`;
          returnObject.label = 'Back to ' + singleResource(resource) + 's';
          break;
      }
    }
    
    return returnObject;
  }

  useEffect(() => {
    handleGetInstance();
  }, [resource, id, action]);

  return {
    instance,
    instanceDetails,
    handleInstanceAction,
    loading,
    error,
    setError,
    parent_id
  };
};

export default useInstance;